import * as React from 'react'

const RELEASE_CLICK_TIMEOUT_MS = 1_000

function getEligibleControl(event: MouseEvent) {
	if (
		event.defaultPrevented ||
		event.button !== 0 ||
		event.metaKey ||
		event.altKey ||
		event.ctrlKey ||
		event.shiftKey ||
		!(event.target instanceof Element)
	) {
		return null
	}

	const control = event.target.closest('[data-navigate-short]')
	if (!(control instanceof HTMLAnchorElement) && !(control instanceof HTMLButtonElement)) return null
	if (control.getAttribute('aria-disabled') === 'true') return null
	if (control instanceof HTMLButtonElement) return control.disabled ? null : control
	if (!control.hasAttribute('href') || control.hasAttribute('download')) return null
	if (control.target && control.target.toLowerCase() !== '_self') return null
	return control
}

export function EarlyLinkActivation() {
	React.useEffect(() => {
		let pendingReleaseControl: HTMLAnchorElement | HTMLButtonElement | null = null
		let releaseTimeout: number | undefined
		let dispatchingEarlyClick = false

		const clearPendingRelease = () => {
			pendingReleaseControl = null
			if (releaseTimeout !== undefined) window.clearTimeout(releaseTimeout)
			releaseTimeout = undefined
		}

		const handleMouseDown = (event: MouseEvent) => {
			const control = getEligibleControl(event)
			if (!control || !control.isConnected) return

			clearPendingRelease()
			pendingReleaseControl = control
			dispatchingEarlyClick = true
			try {
				// Use the control's real click path so React Router and component
				// onClick handlers keep their existing navigation semantics.
				control.click()
			} finally {
				dispatchingEarlyClick = false
			}

			// A drag can prevent the browser from producing a release click.
			// Do not let that stale press suppress a later keyboard activation.
			releaseTimeout = window.setTimeout(clearPendingRelease, RELEASE_CLICK_TIMEOUT_MS)
		}

		const handleClick = (event: MouseEvent) => {
			if (dispatchingEarlyClick || !pendingReleaseControl || !(event.target instanceof Element)) return
			const control = event.target.closest('[data-navigate-short]')
			if (control !== pendingReleaseControl) return

			event.preventDefault()
			event.stopImmediatePropagation()
			clearPendingRelease()
		}

		// Bubble-phase mousedown respects component handlers that cancel a press.
		document.addEventListener('mousedown', handleMouseDown)
		// Capture the browser's later click before React Router sees it a second time.
		document.addEventListener('click', handleClick, true)
		return () => {
			document.removeEventListener('mousedown', handleMouseDown)
			document.removeEventListener('click', handleClick, true)
			clearPendingRelease()
		}
	}, [])

	return null
}
